import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import {
  GameSubTypeForm,
  emptyGameSubTypeForm,
  type GameSubTypeFormValue,
} from '../components/gameForms'
import { Select } from '../components/controls'
import type { GameType } from './GameTypes'

export interface GameSubType {
  gameSubTypeId: number
  gameTypeId: number
  gameType: string
  gameSubType: string
  icon: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface GameSubTypePage {
  gameSubTypes: GameSubType[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * GameSubTypesPage is the platform game-subtype catalogue —
 * [Catalog].[Platform.GameSubType] in the BsConnekto database.
 *
 * Every subtype hangs off a game type, so the parent is a first-class filter
 * (the game-types page links here with ?gameTypeId=) and a required field on
 * every write. Names only need to be unique within their parent.
 */
export function GameSubTypesPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  // The parent filter is in the URL so the game-types page can link into it and
  // the view survives a reload.
  const [searchParams, setSearchParams] = useSearchParams()
  const parentFilter = searchParams.get('gameTypeId') ?? ''

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<GameSubType | null>(null)
  const [form, setForm] = useState<GameSubTypeFormValue>(emptyGameSubTypeForm)
  const [error, setError] = useState('')

  // The type list feeds both the filter and the form; all of them, disabled
  // included, so an existing subtype under a retired type still resolves.
  const { data: typeData } = useQuery({
    queryKey: ['game-types', 'all'],
    queryFn: async () => {
      const res = await api.get('/game-types', { params: { includeDisabled: 'true', limit: 200 } })
      return res.data as { gameTypes: GameType[] }
    },
    staleTime: 60 * 1000,
  })
  const gameTypes = typeData?.gameTypes ?? []

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(parentFilter ? { gameTypeId: parentFilter } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['game-subtypes', params],
    queryFn: async (): Promise<GameSubTypePage> => {
      const res = await api.get('/game-subtypes', { params })
      return res.data as GameSubTypePage
    },
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['game-subtypes'] })
    // The game-types list shows a subtype count.
    qc.invalidateQueries({ queryKey: ['game-types'] })
  }

  const setParentFilter = (value: string) => {
    if (value) searchParams.set('gameTypeId', value)
    else searchParams.delete('gameTypeId')
    setSearchParams(searchParams, { replace: true })
    setOffset(0)
  }

  const openCreate = () => {
    setEditing(null)
    setForm({
      ...emptyGameSubTypeForm,
      gameTypeId: parentFilter || String(gameTypes[0]?.gameTypeId ?? ''),
    })
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (g: GameSubType) => {
    setEditing(g)
    setForm({ gameTypeId: String(g.gameTypeId), gameSubType: g.gameSubType, icon: g.icon })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        gameTypeId: Number(form.gameTypeId),
        gameSubType: form.gameSubType.trim(),
        icon: form.icon.trim(),
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/game-subtypes/${editing.gameSubTypeId}`, body)
        : api.post('/game-subtypes', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('gameSubTypes.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (g: GameSubType) =>
      g.isDisabledByPlatform
        ? api.post(`/game-subtypes/${g.gameSubTypeId}/restore`)
        : api.delete(`/game-subtypes/${g.gameSubTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('gameSubTypes.errors.save'))),
  })

  const subTypes = data?.gameSubTypes ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('gameSubTypes.title')}
        subtitle={t('gameSubTypes.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate} disabled={gameTypes.length === 0}>
                    <Plus />
                    {t('gameSubTypes.newGameSubType')}
                  </Button>
                }
              />
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('gameSubTypes.editGameSubType') : t('gameSubTypes.newGameSubType')}
                  </DialogTitle>
                </DialogHeader>
                <GameSubTypeForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  gameTypes={gameTypes}
                  pending={save.isPending}
                  error={error}
                  submitLabel={editing ? t('common.save') : t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="game-subtypes">
        <p>{t('gameSubTypes.help.line1')}</p>
        <p>{t('gameSubTypes.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('gameSubTypes.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled || parentFilter !== ''}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setIncludeDisabled(true)
          setParentFilter('')
        }}
      >
        <Select
          value={parentFilter}
          onValueChange={setParentFilter}
          items={gameTypes.map((g) => ({ value: String(g.gameTypeId), label: g.gameType }))}
          placeholder={t('gameSubTypes.allGameTypes')}
          className="w-48"
        />
      </CatalogToolbar>

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('gameSubTypes.columns.id')}</TableHead>
                  <TableHead className="w-40">{t('gameSubTypes.columns.gameType')}</TableHead>
                  <TableHead>{t('gameSubTypes.columns.name')}</TableHead>
                  <TableHead className="w-64">{t('gameSubTypes.columns.icon')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {subTypes.map((g) => (
                  <TableRow key={g.gameSubTypeId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {g.gameSubTypeId}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/game-types/${g.gameTypeId}`}
                        className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
                      >
                        {g.gameType}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">{g.gameSubType}</TableCell>
                    <TableCell className="truncate font-mono text-xs text-muted-foreground">
                      {g.icon || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={g.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(g)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={g.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(g)}
                          >
                            {g.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {subTypes.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 6 : 5}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('gameSubTypes.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('gameSubTypes.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
